import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import type { ConformityPreview, ConformitySeriesPreview } from '../computations';
import { MICRON_PER_MM } from '../rockUcsFields';

/**
 * REQ-01 — inline conformity charts (INTERNAL ONLY, never on the PDF report).
 * Styled to match the KeyLAB workbook's Conformity sheet exactly:
 *   • 4 flatness plots — measured markers + dotted best-fit + SOLID ±25 µm
 *     band lines, fixed Y −50…50 µm ("Change in Height"), X = position.
 *   • 2 parallelism plots — both ends' measured points + dotted fit lines
 *     (End 1 = diamonds, End 2 = circles; the workbook uses ×/○).
 *
 * BUG-2026-0112 — readings are entered/displayed in µm, but the preview
 * (band/fit/maxDev) is computed in mm. The Y axis renders in µm, so every
 * height value is scaled ×1000 (MICRON_PER_MM) at plot time only; the
 * underlying mm computation is unchanged. X (position) stays mm.
 */

type SeriesValues = { positionMm: number; valueMm: number | null }[];

function smallChartBase(xMax: number): Highcharts.Options {
  return {
    chart: { height: 150, spacing: [6, 6, 2, 2], animation: false, style: { fontFamily: 'inherit' } },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      title: { text: 'Length Across Diameter (mm)', style: { fontSize: '0.55rem' } },
      min: 0,
      max: xMax,
      tickInterval: 10,
      labels: { style: { fontSize: '0.58rem' } },
      gridLineWidth: 0,
      lineColor: '#999999',
    },
    yAxis: {
      title: { text: 'Change in Height (µm)', style: { fontSize: '0.55rem' } },
      // Fixed −50…50 µm scale (= the workbook's ±0.05 mm). Tick must divide
      // 50 evenly or Highcharts expands the axis to the next tick (±60).
      min: -50,
      max: 50,
      tickInterval: 25,
      labels: { style: { fontSize: '0.58rem' }, format: '{value:.0f}' },
      gridLineWidth: 1,
      gridLineColor: '#e6e6e6',
    },
    tooltip: { valueDecimals: 1 },
    plotOptions: { series: { animation: false } },
  };
}

function FlatnessChart({
  series,
  values,
  title,
}: {
  series: ConformitySeriesPreview;
  values: SeriesValues;
  title: string;
}) {
  const xMax = values.length > 0 ? values[values.length - 1].positionMm : 60;
  const options = useMemo((): Highcharts.Options => ({
    ...smallChartBase(xMax),
    series: [
      {
        type: 'line',
        name: 'Upper band (+25 µm)',
        data: series.band.map((b) => [b.positionMm, b.upperMm * MICRON_PER_MM]),
        color: '#000000',
        lineWidth: 1,
        marker: { enabled: false },
        enableMouseTracking: false,
      },
      {
        type: 'line',
        name: 'Lower band (−25 µm)',
        data: series.band.map((b) => [b.positionMm, b.lowerMm * MICRON_PER_MM]),
        color: '#000000',
        lineWidth: 1,
        marker: { enabled: false },
        enableMouseTracking: false,
      },
      {
        type: 'line',
        name: 'Best fit',
        data: series.band.map((b) => [b.positionMm, b.fitMm * MICRON_PER_MM]),
        color: '#5B7DA8',
        dashStyle: 'Dot',
        lineWidth: 1.5,
        marker: { enabled: false },
      },
      {
        type: 'line',
        name: 'Measured',
        data: values.filter((v) => v.valueMm !== null).map((v) => [v.positionMm, (v.valueMm as number) * MICRON_PER_MM]),
        color: '#1A1A1A',
        lineWidth: 0, // markers only, like the workbook's × points
        marker: { enabled: true, symbol: 'diamond', radius: 3 },
        states: { hover: { lineWidthPlus: 0 } },
      },
    ],
  }), [series, values, xMax]);
  return (
    <Box>
      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', textAlign: 'center' }}>
        {title}
      </Typography>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}

function ParallelismChart({
  end1,
  end2,
  values1,
  values2,
  title,
}: {
  end1: ConformitySeriesPreview | undefined;
  end2: ConformitySeriesPreview | undefined;
  values1: SeriesValues;
  values2: SeriesValues;
  title: string;
}) {
  const xMax = values1.length > 0 ? values1[values1.length - 1].positionMm : 60;
  const options = useMemo((): Highcharts.Options => ({
    ...smallChartBase(xMax),
    series: [
      {
        type: 'line',
        name: 'End 1 measured',
        data: values1.filter((v) => v.valueMm !== null).map((v) => [v.positionMm, (v.valueMm as number) * MICRON_PER_MM]),
        color: '#1A1A1A',
        lineWidth: 0,
        marker: { enabled: true, symbol: 'diamond', radius: 3 },
        states: { hover: { lineWidthPlus: 0 } },
      },
      {
        type: 'line',
        name: 'End 2 measured',
        data: values2.filter((v) => v.valueMm !== null).map((v) => [v.positionMm, (v.valueMm as number) * MICRON_PER_MM]),
        color: '#3B5B7E',
        lineWidth: 0,
        marker: { enabled: true, symbol: 'circle', radius: 3, fillColor: '#FFFFFF', lineWidth: 1, lineColor: '#3B5B7E' },
        states: { hover: { lineWidthPlus: 0 } },
      },
      {
        type: 'line',
        name: 'End 1 best fit',
        data: (end1?.band ?? []).map((b) => [b.positionMm, b.fitMm * MICRON_PER_MM]),
        color: '#1A1A1A',
        dashStyle: 'Dot',
        lineWidth: 1.5,
        marker: { enabled: false },
      },
      {
        type: 'line',
        name: 'End 2 best fit',
        data: (end2?.band ?? []).map((b) => [b.positionMm, b.fitMm * MICRON_PER_MM]),
        color: '#3B5B7E',
        dashStyle: 'Dot',
        lineWidth: 1.5,
        marker: { enabled: false },
      },
    ],
  }), [end1, end2, values1, values2, xMax]);
  return (
    <Box>
      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', textAlign: 'center' }}>
        {title}
      </Typography>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}

interface Props {
  preview: ConformityPreview;
  values: Record<string, SeriesValues>; // key "end-line"
}

export const ConformityCharts = ({ preview, values }: Props) => {
  const find = (end: number, line: number) =>
    preview.series.find((s) => s.endNumber === end && s.diameterLine === line);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
      {[
        { end: 1, line: 1, title: 'Flatness End 1, Dia 1' },
        { end: 1, line: 2, title: 'Flatness End 1, Dia 2' },
        { end: 2, line: 1, title: 'Flatness End 2, Dia 1' },
        { end: 2, line: 2, title: 'Flatness End 2, Dia 2' },
      ].map(({ end, line, title }) => {
        const s = find(end, line);
        return s && s.band.length > 0 ? (
          <FlatnessChart key={title} series={s} values={values[`${end}-${line}`] ?? []} title={title} />
        ) : (
          <Box key={title} sx={{ height: 168, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.disabled">{title} — enter readings</Typography>
          </Box>
        );
      })}
      <ParallelismChart
        end1={find(1, 1)} end2={find(2, 1)}
        values1={values['1-1'] ?? []} values2={values['2-1'] ?? []}
        title="Parallelism Diameter 1 (End 1 vs End 2)"
      />
      <ParallelismChart
        end1={find(1, 2)} end2={find(2, 2)}
        values1={values['1-2'] ?? []} values2={values['2-2'] ?? []}
        title="Parallelism Diameter 2 (End 1 vs End 2)"
      />
    </Box>
  );
};
